import { useMemo } from 'react'
import type { RemakeVariant, VariantEdits } from '@/lib/api/amazon-listing.types'
import type { CompiledRules } from '@/hooks/validation.types'
import { useTextValidation } from '@/hooks/useTextValidation'
import type { Violation } from '@/hooks/useTextValidation'
import { useTranslation } from 'react-i18next'
import { RemakeFieldGroup } from './RemakeFieldGroup'
import { RemakeComplianceSummary } from './RemakeComplianceSummary'

interface RemakeVariantViewProps {
  variant: RemakeVariant
  variantIndex: number
  edits: VariantEdits
  onEditChange: (vi: number, field: string, value: string | string[]) => void
  onResetField: (vi: number, field: string) => void
  rules: CompiledRules | undefined
}

export function RemakeVariantView({ variant, variantIndex, edits, onEditChange, onResetField, rules }: RemakeVariantViewProps) {
  const { t } = useTranslation()
  const titleValue = (edits.title as string) ?? variant.title ?? ''
  const descValue = (edits.description as string) ?? variant.description ?? ''

  const titleV = useTextValidation(titleValue, rules?.title)
  const descV = useTextValidation(descValue, rules?.description)

  const allViolations = useMemo<Violation[]>(
    () => [...titleV.violations, ...descV.violations],
    [titleV.violations, descV.violations],
  )

  return (
    <div className="space-y-4">
      <RemakeFieldGroup
        variant={variant}
        variantIndex={variantIndex}
        edits={edits as Record<string, string | string[] | null | undefined>}
        onEditChange={onEditChange}
        onResetField={onResetField}
        rules={rules}
      />

      {variant.reasoning && (
        <div className="text-xs bg-neutral-50 dark:bg-neutral-800 rounded-lg p-3">
          <span className="font-semibold text-neutral-700 dark:text-neutral-300">{t('remake.reasoningLabel')}</span>{' '}
          <span className="text-neutral-500 dark:text-neutral-400 italic">{variant.reasoning}</span>
        </div>
      )}

      <RemakeComplianceSummary violations={allViolations} />
    </div>
  )
}
